import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import { patternService } from '../services/patternService';

// ─── helpers ─────────────────────────────────────────────────────────────────

const uid = () => crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);

const DEFAULT_ABBREVIATIONS = [
  { id: uid(), code: 'MR',    definition: 'Magic ring',                       selected: true  },
  { id: uid(), code: 'sc',    definition: 'Single crochet',                   selected: true  },
  { id: uid(), code: 'dc',    definition: 'Double crochet',                   selected: false },
  { id: uid(), code: 'hdc',   definition: 'Half double crochet',              selected: false },
  { id: uid(), code: 'inc',   definition: 'Increase — 2 sc in same stitch',   selected: true  },
  { id: uid(), code: 'dec',   definition: 'Invisible decrease',               selected: true  },
  { id: uid(), code: 'sl st', definition: 'Slip stitch',                      selected: true  },
  { id: uid(), code: 'ch',    definition: 'Chain',                            selected: true  },
  { id: uid(), code: 'st(s)', definition: 'Stitch(es)',                        selected: true  },
  { id: uid(), code: 'BLO',   definition: 'Back loop only',                   selected: false },
  { id: uid(), code: 'FLO',   definition: 'Front loop only',                  selected: false },
  { id: uid(), code: 'pm',    definition: 'Place marker',                     selected: false },
  { id: uid(), code: 'FO',    definition: 'Fasten off',                       selected: true  },
];

const INITIAL_PATTERN = {
  title: '',
  subtitle: '',
  description: '',
  animal_type: '',
  difficulty: 'beginner',
  finished_size: '',
  gauge: '',
  pattern_notes: '',
  language: 'us',
  theme: 'minimal',
  materials: [],
  abbreviations: DEFAULT_ABBREVIATIONS,
  sections: [],
  photos: [],
  branding: {
    shop_name: '',
    logo_url: '',
    instagram: '',
    website: '',
    copyright: '',
    accent_color: '#C4B8A8',
    font_style: 'minimal',
  },
};

const initialState = {
  patternId: null,
  pattern: { ...INITIAL_PATTERN },
  currentStep: 1,
  isDirty: false,
  isSaving: false,
  lastSaved: null,
  saveError: null,
  previewPage: 0,
  previewMode: 'desktop',
  previewZoom: 0.75,
  exportStatus: 'idle',
};

// ─── reducer ─────────────────────────────────────────────────────────────────

function reducer(state, action) {
  switch (action.type) {

    // ── navigation ──────────────────────────────────────────────────────────
    case 'SET_STEP':
      return { ...state, currentStep: action.step };

    // ── load / reset ────────────────────────────────────────────────────────
    case 'LOAD_PATTERN':
      return {
        ...state,
        patternId: action.id,
        pattern: { ...INITIAL_PATTERN, ...action.data },
        isDirty: false,
        lastSaved: action.savedAt || null,
      };
    case 'RESET':
      return { ...initialState, pattern: { ...INITIAL_PATTERN } };

    // ── basic fields ────────────────────────────────────────────────────────
    case 'SET_FIELD':
      return {
        ...state,
        pattern: { ...state.pattern, [action.field]: action.value },
        isDirty: true,
      };

    // ── materials ───────────────────────────────────────────────────────────
    case 'ADD_MATERIAL': {
      const mat = { id: uid(), category: 'yarn', name: '', amount: '', notes: '' };
      return {
        ...state,
        pattern: { ...state.pattern, materials: [...state.pattern.materials, mat] },
        isDirty: true,
      };
    }
    case 'UPDATE_MATERIAL': {
      const mats = state.pattern.materials.map(m =>
        m.id === action.id ? { ...m, ...action.changes } : m
      );
      return { ...state, pattern: { ...state.pattern, materials: mats }, isDirty: true };
    }
    case 'REMOVE_MATERIAL':
      return {
        ...state,
        pattern: { ...state.pattern, materials: state.pattern.materials.filter(m => m.id !== action.id) },
        isDirty: true,
      };
    case 'REORDER_MATERIALS':
      return { ...state, pattern: { ...state.pattern, materials: action.materials }, isDirty: true };

    // ── abbreviations ────────────────────────────────────────────────────────
    case 'TOGGLE_ABBREVIATION': {
      const abbrs = state.pattern.abbreviations.map(a =>
        a.id === action.id ? { ...a, selected: !a.selected } : a
      );
      return { ...state, pattern: { ...state.pattern, abbreviations: abbrs }, isDirty: true };
    }
    case 'ADD_CUSTOM_ABBREVIATION': {
      const custom = { id: uid(), code: '', definition: '', selected: true, isCustom: true };
      return {
        ...state,
        pattern: { ...state.pattern, abbreviations: [...state.pattern.abbreviations, custom] },
        isDirty: true,
      };
    }
    case 'UPDATE_ABBREVIATION': {
      const abbrs = state.pattern.abbreviations.map(a =>
        a.id === action.id ? { ...a, ...action.changes } : a
      );
      return { ...state, pattern: { ...state.pattern, abbreviations: abbrs }, isDirty: true };
    }
    case 'REMOVE_ABBREVIATION':
      return {
        ...state,
        pattern: {
          ...state.pattern,
          abbreviations: state.pattern.abbreviations.filter(a => a.id !== action.id),
        },
        isDirty: true,
      };

    // ── sections ─────────────────────────────────────────────────────────────
    case 'ADD_SECTION': {
      const sec = {
        id: uid(),
        title: action.title || 'New Section',
        color: action.color || '#C4B8A8',
        notes: '',
        rounds: [],
      };
      return {
        ...state,
        pattern: { ...state.pattern, sections: [...state.pattern.sections, sec] },
        isDirty: true,
      };
    }
    case 'UPDATE_SECTION': {
      const secs = state.pattern.sections.map(s =>
        s.id === action.id ? { ...s, ...action.changes } : s
      );
      return { ...state, pattern: { ...state.pattern, sections: secs }, isDirty: true };
    }
    case 'REMOVE_SECTION':
      return {
        ...state,
        pattern: { ...state.pattern, sections: state.pattern.sections.filter(s => s.id !== action.id) },
        isDirty: true,
      };
    case 'REORDER_SECTIONS':
      return { ...state, pattern: { ...state.pattern, sections: action.sections }, isDirty: true };

    // ── rounds ───────────────────────────────────────────────────────────────
    case 'ADD_ROUND': {
      const secs = state.pattern.sections.map(s => {
        if (s.id !== action.sectionId) return s;
        const round = {
          id: uid(),
          label: `Round ${s.rounds.length + 1}`,
          instruction: '',
          stitch_count: '',
          type: action.roundType || 'round',
          image_url: '',
        };
        return { ...s, rounds: [...s.rounds, round] };
      });
      return { ...state, pattern: { ...state.pattern, sections: secs }, isDirty: true };
    }
    case 'UPDATE_ROUND': {
      const secs = state.pattern.sections.map(s => {
        if (s.id !== action.sectionId) return s;
        return {
          ...s,
          rounds: s.rounds.map(r => r.id === action.roundId ? { ...r, ...action.changes } : r),
        };
      });
      return { ...state, pattern: { ...state.pattern, sections: secs }, isDirty: true };
    }
    case 'REMOVE_ROUND': {
      const secs = state.pattern.sections.map(s => {
        if (s.id !== action.sectionId) return s;
        return { ...s, rounds: s.rounds.filter(r => r.id !== action.roundId) };
      });
      return { ...state, pattern: { ...state.pattern, sections: secs }, isDirty: true };
    }
    case 'REORDER_ROUNDS': {
      const secs = state.pattern.sections.map(s =>
        s.id === action.sectionId ? { ...s, rounds: action.rounds } : s
      );
      return { ...state, pattern: { ...state.pattern, sections: secs }, isDirty: true };
    }

    // ── photos ───────────────────────────────────────────────────────────────
    case 'ADD_PHOTO': {
      const photo = {
        id: uid(),
        url: action.url,
        category: action.category || 'cover',
        caption: '',
        position: state.pattern.photos.length,
      };
      return {
        ...state,
        pattern: { ...state.pattern, photos: [...state.pattern.photos, photo] },
        isDirty: true,
      };
    }
    case 'UPDATE_PHOTO': {
      const photos = state.pattern.photos.map(p =>
        p.id === action.id ? { ...p, ...action.changes } : p
      );
      return { ...state, pattern: { ...state.pattern, photos }, isDirty: true };
    }
    case 'REMOVE_PHOTO':
      return {
        ...state,
        pattern: { ...state.pattern, photos: state.pattern.photos.filter(p => p.id !== action.id) },
        isDirty: true,
      };
    case 'REORDER_PHOTOS':
      return { ...state, pattern: { ...state.pattern, photos: action.photos }, isDirty: true };

    // ── branding ─────────────────────────────────────────────────────────────
    case 'SET_BRANDING':
      return {
        ...state,
        pattern: { ...state.pattern, branding: { ...state.pattern.branding, ...action.changes } },
        isDirty: true,
      };

    // ── save state ───────────────────────────────────────────────────────────
    case 'SAVE_START':
      return { ...state, isSaving: true, saveError: null };
    case 'SAVE_SUCCESS':
      return { ...state, isSaving: false, isDirty: false, lastSaved: new Date(), saveError: null };
    case 'SAVE_ERROR':
      return { ...state, isSaving: false, saveError: action.error };

    // ── preview controls ─────────────────────────────────────────────────────
    case 'SET_PREVIEW_PAGE':
      return { ...state, previewPage: action.page };
    case 'SET_PREVIEW_MODE':
      return { ...state, previewMode: action.mode };
    case 'SET_PREVIEW_ZOOM':
      return { ...state, previewZoom: action.zoom };

    // ── export ───────────────────────────────────────────────────────────────
    case 'SET_EXPORT_STATUS':
      return { ...state, exportStatus: action.status };

    default:
      return state;
  }
}

// ─── context ─────────────────────────────────────────────────────────────────

const PatternStudioContext = createContext(null);

export function PatternStudioProvider({ children, patternId }) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    patternId: patternId || null,
  });

  const autoSaveTimer = useRef(null);

  // Load existing pattern on mount
  useEffect(() => {
    if (!patternId) return;
    patternService.getById(patternId).then(data => {
      if (data) {
        dispatch({
          type: 'LOAD_PATTERN',
          id: data.id,
          data: { title: data.title, ...(data.pattern_data || {}) },
          savedAt: data.updated_at,
        });
      }
    });
  }, [patternId]);

  // Auto-save every 30s when dirty
  const save = useCallback(async (force = false) => {
    if (!state.isDirty && !force) return;
    dispatch({ type: 'SAVE_START' });
    try {
      const payload = {
        title: state.pattern.title || 'Untitled Pattern',
        pattern_data: state.pattern,
      };
      let id = state.patternId;
      if (id) {
        await patternService.update(id, payload);
      } else {
        const created = await patternService.create(payload);
        id = created.id;
        dispatch({ type: 'LOAD_PATTERN', id, data: state.pattern, savedAt: new Date() });
      }
      dispatch({ type: 'SAVE_SUCCESS' });
      return id;
    } catch (err) {
      dispatch({ type: 'SAVE_ERROR', error: err.message });
    }
  }, [state.isDirty, state.patternId, state.pattern]);

  useEffect(() => {
    if (!state.isDirty) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => save(), 30000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [state.isDirty, save]);

  return (
    <PatternStudioContext.Provider value={{ state, dispatch, save }}>
      {children}
    </PatternStudioContext.Provider>
  );
}

export function usePatternStudio() {
  const ctx = useContext(PatternStudioContext);
  if (!ctx) throw new Error('usePatternStudio must be used inside PatternStudioProvider');
  return ctx;
}
